import { TICKER_CONSTANTS } from "../constants/tickerConstants";

/**
 * Processes a comma-separated alias string into an array of clean aliases
 * @param aliasString - The comma-separated string of aliases
 * @returns Array of processed aliases
 */
export const processAliasString = (aliasString: string): string[] => {
  if (!aliasString?.trim()) return [];

  return aliasString
    .split(",")
    .map((alias) => alias.trim())
    .filter((alias) => alias.length > 0);
};

/**
 * Formats an array of aliases for display as a comma-separated string
 * @param aliases - Array of alias strings
 * @returns Formatted string for display
 */
export const formatAliasesForDisplay = (aliases?: string[]): string => {
  return aliases?.join(", ") ?? "";
};

/**
 * Validates alias format and checks for duplicates
 * @param aliasString - The comma-separated string of aliases
 * @returns Validation result with success flag and error message
 */
export const validateAliases = (aliasString: string): { isValid: boolean; error?: string } => {
  if (!aliasString?.trim()) {
    return { isValid: true }; // Empty is valid since aliases are optional
  }

  if (aliasString.length > TICKER_CONSTANTS.FORM.ALIASES_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Aliases text is too long (max ${TICKER_CONSTANTS.FORM.ALIASES_MAX_LENGTH} characters)`,
    };
  }

  const aliases = processAliasString(aliasString);
  const uniqueAliases = new Set(aliases);

  // Check for duplicates
  if (aliases.length !== uniqueAliases.size) {
    return {
      isValid: false,
      error: "Duplicate aliases are not allowed",
    };
  }

  // Check individual alias format and length
  const invalidAliases = aliases.filter(
    (alias) => alias.length > TICKER_CONSTANTS.FORM.ALIAS_MAX_LENGTH || !TICKER_CONSTANTS.FORM.ALIAS_PATTERN.test(alias)
  );

  if (invalidAliases.length > 0) {
    return {
      isValid: false,
      error: `Invalid aliases (max ${TICKER_CONSTANTS.FORM.ALIAS_MAX_LENGTH} chars, alphanumeric only): ${invalidAliases.join(", ")}`,
    };
  }

  return { isValid: true };
};
